// ---------------------------------------------------------------------------
// V11Routes — V11 module route table
// ---------------------------------------------------------------------------
// FEL-39: each route is React.lazy()'d so the FE bundle splits per-page.
// Before the split the production bundle was 779 KB gzip in a single
// chunk — every user opening /v11/feed downloaded Discovery + Funnel +
// Compare + Channels + Recharts up front. With lazy routes the Feed and
// Lens routes drop to roughly 100–150 KB gzip each.
//
// Suspense fallback is `null` — each page already renders its own
// skeleton on first paint, so a generic spinner would compete with that.
// ---------------------------------------------------------------------------

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

const FeltonDiscovery = lazy(() => import('./felton/FeltonDiscovery').then(m => ({ default: m.FeltonDiscovery })));
const FeltonChannels = lazy(() => import('./felton/FeltonChannels').then(m => ({ default: m.FeltonChannels })));
const FeltonSegments = lazy(() => import('./felton/FeltonSegments').then(m => ({ default: m.FeltonSegments })));
const FeltonCompare = lazy(() => import('./felton/FeltonCompare').then(m => ({ default: m.FeltonCompare })));
const FeltonOverlap = lazy(() => import('./felton/FeltonOverlap').then(m => ({ default: m.FeltonOverlap })));
const FeltonReports = lazy(() => import('./felton/FeltonReports').then(m => ({ default: m.FeltonReports })));
const FeltonFunnel = lazy(() => import('./felton/FeltonFunnel').then(m => ({ default: m.FeltonFunnel })));
const FeltonContentUniverse = lazy(() => import('./felton/FeltonContentUniverse').then(m => ({ default: m.FeltonContentUniverse })));
const FeltonFeed = lazy(() => import('./felton/FeltonFeed').then(m => ({ default: m.FeltonFeed })));
const FeltonDashboard = lazy(() => import('./felton/FeltonDashboard').then(m => ({ default: m.FeltonDashboard })));
const FeltonOverview = lazy(() => import('./felton/FeltonOverview').then(m => ({ default: m.FeltonOverview })));
const FeltonLens = lazy(() => import('./felton/FeltonLens').then(m => ({ default: m.FeltonLens })));
const FeltonStory = lazy(() => import('./felton/FeltonStory').then(m => ({ default: m.FeltonStory })));
const ProfilePage = lazy(() => import('@/pages/Profile'));
const SettingsPage = lazy(() => import('@/pages/Settings'));

export const V11Routes: React.FC = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Default redirect to Feed (primary entry point as of 2026-05-15) */}
        <Route index element={<Navigate to="/v11/feed" replace />} />

        {/* V11 Module Routes */}
        <Route path="discovery" element={<FeltonDiscovery />} />
        <Route path="channels" element={<FeltonChannels />} />
        {/* FEL-111: o Dashboard (comparação de canais). Distinto de
            /channels, que é a gestão dos canais monitorizados (adicionar,
            activar, remover). Era /overview até 2026-09-22; desde então é
            uma cópia própria (FeltonDashboard, /api/v11/dashboard/channels)
            e o nome Overview fica para o ecrã que evolui à parte. */}
        <Route path="dashboard" element={<FeltonDashboard />} />
        <Route path="overview" element={<FeltonOverview />} />
        <Route path="funnel" element={<FeltonFunnel />} />
        <Route path="funnel/explore" element={<FeltonContentUniverse />} />
        <Route path="feed" element={<FeltonFeed />} />
        <Route path="feed/lens/:postId" element={<FeltonLens />} />
        <Route path="story/:postId" element={<FeltonStory />} />
        <Route path="audience" element={<FeltonSegments />} />
        <Route path="compare" element={<FeltonCompare />} />
        <Route path="overlap" element={<FeltonOverlap />} />
        <Route path="reports" element={<FeltonReports />} />

        {/* User Management Routes */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />

        {/* Legacy redirects */}
        <Route path="felton/explore" element={<Navigate to="/v11/discovery" replace />} />
        <Route path="felton/segments" element={<Navigate to="/v11/audience" replace />} />
        <Route path="felton/compare" element={<Navigate to="/v11/compare" replace />} />
        <Route path="felton/overlap" element={<Navigate to="/v11/overlap" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/v11/feed" replace />} />
      </Routes>
    </Suspense>
  );
};
